import {dataTestId} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {MyLocation} from '@mui/icons-material'
import {Stack, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {
  useAllStrengthMeasurementUnits,
  formatLabelWithUnit
} from '../hooks/useStrengthMeasurementUnit'
import {useStrengthTargetOverviewUtils} from '../hooks/useStrengthTargetOverviewUtils'
import {DayPeriod, StrengthTarget} from '../types'

interface StrengthTargetOverviewDesktopTableProps {
  targets: StrengthTarget[]
  timezone: string
  dayPeriods: DayPeriod[]
}

interface OverviewRowData {
  dayPeriod: DayPeriod
  currentTarget: StrengthTarget | undefined
  unit: string | undefined
}

export const StrengthTargetOverviewDesktopTable: React.FC<
  StrengthTargetOverviewDesktopTableProps
> = ({targets, timezone, dayPeriods}) => {
  const {t} = useTranslation()

  const units = useAllStrengthMeasurementUnits()
  const headerUnit = units[1] || units[2] || units[3] || units[7] || units[28]

  const {getCurrentTargetForPeriod, formatDate, formatValidFrom} = useStrengthTargetOverviewUtils(
    targets,
    timezone
  )

  const tableData: OverviewRowData[] = useMemo(
    () =>
      dayPeriods.map((dayPeriod) => ({
        dayPeriod,
        currentTarget: getCurrentTargetForPeriod(dayPeriod),
        unit: units[dayPeriod]
      })),
    [dayPeriods, getCurrentTargetForPeriod, units]
  )

  const columns: Column<OverviewRowData>[] = useMemo(
    () => [
      {
        key: 'day',
        label: t('materialsSettings.strengthTargets.day'),
        customTemplate: (row) => (
          <Typography sx={{fontWeight: 500}} {...dataTestId(`overview_cell_day_${row.dayPeriod}`)}>
            {t('materialsSettings.strengthTargets.dayPeriod', {count: row.dayPeriod})}
          </Typography>
        ),
        headerElAdditionalProps: {...dataTestId('overview_header_day')} as any
      },
      {
        key: 'currentTarget',
        label: formatLabelWithUnit(
          t('materialsSettings.strengthTargets.currentTarget'),
          headerUnit
        ),
        customTemplate: (row) => {
          if (row.currentTarget?.target !== undefined) {
            return (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                {...dataTestId(`overview_cell_target_${row.dayPeriod}`)}
              >
                <MyLocation
                  fontSize="small"
                  sx={{
                    color: (theme) => theme.palette.primary.main
                  }}
                  {...dataTestId(`overview_current_icon_${row.dayPeriod}`)}
                />
                <Typography>
                  {row.currentTarget.target}
                  {row.unit ? ` ${row.unit}` : ''}
                </Typography>
              </Stack>
            )
          }
          return <span {...dataTestId(`overview_cell_target_${row.dayPeriod}`)}>-</span>
        },
        headerElAdditionalProps: {id: 'overview_header_current_target'}
      },
      {
        key: 'lowerBound',
        label: formatLabelWithUnit(t('materialsSettings.strengthTargets.lowerBound'), headerUnit),
        customTemplate: (row) => {
          const value =
            row.currentTarget?.lowerBound !== undefined
              ? `${row.currentTarget.lowerBound}${row.unit ? ` ${row.unit}` : ''}`
              : '-'
          return <span {...dataTestId(`overview_cell_lower_bound_${row.dayPeriod}`)}>{value}</span>
        },
        headerElAdditionalProps: {id: 'overview_header_lower_bound'}
      },
      {
        key: 'upperBound',
        label: formatLabelWithUnit(t('materialsSettings.strengthTargets.upperBound'), headerUnit),
        customTemplate: (row) => {
          const value =
            row.currentTarget?.upperBound !== undefined
              ? `${row.currentTarget.upperBound}${row.unit ? ` ${row.unit}` : ''}`
              : '-'
          return <span {...dataTestId(`overview_cell_upper_bound_${row.dayPeriod}`)}>{value}</span>
        },
        headerElAdditionalProps: {id: 'overview_header_upper_bound'}
      },
      {
        key: 'validFrom',
        label: t('materialsSettings.strengthTargets.validFrom'),
        customTemplate: (row) => {
          const value = row.currentTarget ? formatValidFrom(row.currentTarget.validFrom) : '-'
          return <span {...dataTestId(`overview_cell_valid_from_${row.dayPeriod}`)}>{value}</span>
        },
        headerElAdditionalProps: {id: 'overview_header_valid_from'}
      },
      {
        key: 'validTo',
        label: t('materialsSettings.strengthTargets.validTo'),
        customTemplate: (row) => {
          const value = row.currentTarget?.validTo ? formatDate(row.currentTarget.validTo) : '-'
          return <span {...dataTestId(`overview_cell_valid_to_${row.dayPeriod}`)}>{value}</span>
        },
        headerElAdditionalProps: {id: 'overview_header_valid_to'}
      }
    ],
    [t, headerUnit, formatDate, formatValidFrom]
  )

  return (
    <DataTable
      columns={columns}
      data={tableData}
      keyExtractor={(row) => String(row.dayPeriod)}
      rowSx={() => ({
        '&:hover': {
          backgroundColor: (theme) => theme.palette.action.hover
        }
      })}
      tableContainerSx={{
        '& .MuiTableHead-root .MuiTableRow-root': {
          backgroundColor: (theme) => theme.palette.grey[50]
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          fontWeight: 600
        }
      }}
      data-test-id="strength_targets_overview_table"
    />
  )
}
